import React, { useState } from "react";
import { Alert, Button, Card, DatePicker, Descriptions, Empty, Input, Modal, Radio, Select, Table, Tag } from "antd";
import { SearchOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_YYYY_MM_DD } from "constants/DateConstant";
import { cancelSooCoolOrders, searchSooCoolOrders } from "../../../functions/api/soocool";
import {
  formatSooCoolAddress,
  formatSooCoolAddressee,
  formatSooCoolGoodWeight,
  formatSooCoolTimeWindow,
  getSooCoolColliCount,
  getSooCoolCompletionProblem,
  getSooCoolDeliveredAtNeighbours,
  getSooCoolDeliveryTask,
  getSooCoolNeighbourHouseNumber,
  getSooCoolPlannedTimeWindow,
  getSooCoolTaskStateColor,
  getSooCoolTrackAndTraceLink,
  getSooCoolTransportRequirementColor,
  SOOCOOL_TASK_TYPE_PICKUP,
} from "../../../functions/soocool";

const SEARCH_MODE_DATE = "date";
const SEARCH_MODE_REFERENCE = "orderReference";
const SEARCH_MODE_BARCODE = "barcode";

const DATE_FIELD_DELIVERY = "deliveryDate";
const DATE_FIELD_PICKUP = "pickupDate";

/**
 * An api error that really means "SooCool would not talk to us": SooCool answers 401 when the
 * API key is missing or wrong, and that arrives here as plain text in the api's message. Worth
 * separating out, because it is the one failure staff cannot fix from this screen.
 */
const looksLikeAuthProblem = (messageText) => /401|unauthori|api[-\s]?key|api-sleutel/i.test(messageText || "");

/**
 * Window on SooCool: what it holds for a date, an order number or a barcode, without logging into
 * SooCool's portal - and cancelling selected shipments there.
 *
 * Searching asks our own api (`GET /api/shipping/soocool/search`), which proxies SooCool's
 * `GET /order` with the server-side API key. Cancelling (`POST /api/shipping/soocool/cancel`) also
 * releases our own order of each shipment, so it can be exported again. Nothing here creates a
 * shipment: exporting is done on the orders page.
 *
 * The shop choice is not cosmetic: `api/` is deployed once per shop (planning/soocool-koppeling.md,
 * besluit 4), so a search always runs against exactly one shop's deploy, its own Firebase token
 * and its own SooCool account.
 */
const SooCool = () => {
  const { t, i18n } = useTranslation();

  const [shopSource, setShopSource] = useState("VCE");
  const [searchMode, setSearchMode] = useState(SEARCH_MODE_DATE);
  const [dateField, setDateField] = useState(DATE_FIELD_DELIVERY);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [orderReference, setOrderReference] = useState("");
  const [barcode, setBarcode] = useState("");

  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // SooCool order ids of the selected rows, and how the last cancellation went
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [cancelOutcome, setCancelOutcome] = useState(null);

  // The results belong to one shop's SooCool account: switching shop throws them away, so a
  // cancellation can never be sent to the other shop's api.
  const changeShop = (value) => {
    setShopSource(value);
    setResults([]);
    setSelectedOrderIds([]);
    setHasSearched(false);
    setCancelOutcome(null);
  };

  /**
   * Exactly one filter per search. SooCool ANDs its query parameters, so sending a leftover date
   * along with an order number would silently return nothing whenever the date does not match.
   */
  const buildFilters = () => {
    if (searchMode === SEARCH_MODE_REFERENCE) return { orderReference: orderReference.trim() };
    if (searchMode === SEARCH_MODE_BARCODE) return { barcode: barcode.trim() };
    if (!selectedDate) return {};
    return { [dateField]: selectedDate.format(DATE_FORMAT_YYYY_MM_DD) };
  };

  /** @param {{keepOutcome?: boolean}} options - true for the refresh right after a cancellation */
  const handleSearch = async ({ keepOutcome = false } = {}) => {
    const filters = buildFilters();
    const hasValue = Object.values(filters).some((value) => `${value ?? ""}`.trim() !== "");

    if (!hasValue) {
      setError(t("soocool.search.missing_input"));
      return;
    }

    setIsLoading(true);
    setError(null);
    setSelectedOrderIds([]);
    if (!keepOutcome) setCancelOutcome(null);
    try {
      const found = await searchSooCoolOrders(shopSource, filters);
      setResults(found);
      setHasSearched(true);
    } catch (err) {
      setResults([]);
      // Not a completed search: leave hasSearched alone so the table does not claim
      // "SooCool heeft hier geen zendingen voor" underneath the error alert.
      setHasSearched(false);
      setError(err?.message || t("soocool.search.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const cancelSelected = async (orderIds) => {
    setCancelling(true);
    setCancelOutcome(null);
    try {
      const outcome = await cancelSooCoolOrders(shopSource, orderIds);
      setCancelOutcome({
        cancelled: outcome.filter((result) => result.status === "cancelled"),
        failed: outcome.filter((result) => result.status !== "cancelled"),
      });
      await handleSearch({ keepOutcome: true });
    } catch (err) {
      setCancelOutcome({ cancelled: [], failed: [], error: err?.message || t("soocool.search.failed") });
    } finally {
      setCancelling(false);
    }
  };

  const confirmCancel = () => {
    const chosen = results.filter((soocoolOrder) => selectedOrderIds.includes(soocoolOrder?.orderId));
    if (chosen.length === 0) return;
    Modal.confirm({
      title: t("soocool.search.cancel_confirm_title", { count: chosen.length }),
      content: (
        <>
          <p>{t("soocool.search.cancel_confirm_description")}</p>
          <p>{chosen.map((soocoolOrder) => soocoolOrder.orderReference || soocoolOrder.orderId).join(", ")}</p>
        </>
      ),
      okText: t("soocool.search.cancel_confirm_ok"),
      okButtonProps: { danger: true },
      cancelText: t("soocool.search.cancel_confirm_back"),
      onOk: () => cancelSelected(chosen.map((soocoolOrder) => soocoolOrder.orderId)),
    });
  };

  /** "HVC-12345 (SooCool 678): what happened to our order, or why SooCool refused". */
  const describeCancelResult = (result) => {
    const detail = result.error || result.note || (result.releasedDocumentId ? t("soocool.search.cancel_released") : null);
    return `${result.orderReference || "?"} (SooCool ${result.sooCoolOrderId})${detail ? `: ${detail}` : ""}`;
  };

  /**
   * A pickup is `delivered` once SooCool has the boxes at the warehouse, so it reads "Opgehaald": next
   * to the delivery of the same order, "Bezorgd" would say the customer has them.
   */
  const renderTaskState = (taskState, taskType) => {
    if (!taskState) return <span>-</span>;
    const pickupKey = `soocool.pickup_task_state.${taskState}`;
    const label =
      taskType === SOOCOOL_TASK_TYPE_PICKUP && i18n.exists(pickupKey)
        ? t(pickupKey)
        : t(`soocool.task_state.${taskState}`, { defaultValue: taskState });
    return <Tag color={getSooCoolTaskStateColor(taskState)}>{label}</Tag>;
  };

  const colliColumns = [
    {
      title: "#",
      width: 50,
      align: "center",
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: t("soocool.barcode"),
      dataIndex: "barcode",
      render: (value) => <span style={{ fontFamily: "monospace" }}>{value || "-"}</span>,
    },
    {
      title: t("weight"),
      align: "right",
      render: (_, good) => <span>{formatSooCoolGoodWeight(good) || "-"}</span>,
    },
    {
      title: t("soocool.contents"),
      dataIndex: "contents",
      render: (value) => <span>{value || "-"}</span>,
    },
    {
      title: t("soocool.transport_requirements"),
      dataIndex: "transportRequirements",
      render: (requirements) => {
        const list = Array.isArray(requirements) ? requirements : [];
        if (list.length === 0) return <span>-</span>;
        return list.map((requirement) => (
          <Tag key={requirement} color={getSooCoolTransportRequirementColor(requirement)}>
            {t(`soocool.transport_requirement.${requirement}`, { defaultValue: requirement })}
          </Tag>
        ));
      },
    },
  ];

  const columns = [
    {
      title: t("soocool.search.column_order_reference"),
      dataIndex: "orderReference",
      render: (value) => <span className="font-weight-semibold">{value || "-"}</span>,
    },
    {
      title: t("soocool.our_reference"),
      dataIndex: "ourReference",
      render: (value) => <span>{value || "-"}</span>,
    },
    {
      title: t("soocool.order_id"),
      dataIndex: "orderId",
      render: (value) => <span style={{ fontFamily: "monospace" }}>{value ?? "-"}</span>,
    },
    {
      title: t("soocool.task_state_label"),
      render: (_, soocoolOrder) => (
        <>
          {renderTaskState(getSooCoolDeliveryTask(soocoolOrder)?.taskState)}
          {!!soocoolOrder.cancelled && <Tag color="red">{t("soocool.search.cancelled")}</Tag>}
        </>
      ),
    },
    {
      title: t("soocool.search.column_colli"),
      align: "center",
      render: (_, soocoolOrder) => <span>{getSooCoolColliCount(soocoolOrder)}</span>,
    },
    {
      title: t("delivery_address"),
      render: (_, soocoolOrder) => {
        const task = getSooCoolDeliveryTask(soocoolOrder);
        const addressee = formatSooCoolAddressee(task?.address);
        const line = formatSooCoolAddress(task?.address);
        if (!addressee && !line) return <span>-</span>;
        return (
          <span>
            {!!addressee && (
              <>
                {addressee}
                <br />
              </>
            )}
            {line || "-"}
          </span>
        );
      },
    },
    {
      title: t("soocool.search.column_time_window"),
      render: (_, soocoolOrder) => {
        const task = getSooCoolDeliveryTask(soocoolOrder);
        const planned = formatSooCoolTimeWindow(getSooCoolPlannedTimeWindow(task));
        const requested = formatSooCoolTimeWindow(task?.timeWindow);
        if (!planned && !requested) return <span>-</span>;
        return (
          <span>
            {planned || requested}
            {!!planned && <Tag style={{ marginLeft: 4 }}>{t("soocool.search.planned")}</Tag>}
          </span>
        );
      },
    },
    {
      title: t("soocool.track_and_trace_label"),
      render: (_, soocoolOrder) => {
        const link = getSooCoolTrackAndTraceLink(soocoolOrder);
        if (!link) return <span style={{ color: "#8c8c8c" }}>-</span>;
        return (
          <a href={link} target="_blank" rel="noopener noreferrer">
            {t("soocool.track_and_trace")}
          </a>
        );
      },
    },
  ];

  /** The task block of an expanded row - one per task, because a shipment can hold more than one. */
  const renderTask = (task, index) => {
    const planned = formatSooCoolTimeWindow(getSooCoolPlannedTimeWindow(task));
    const requested = formatSooCoolTimeWindow(task?.timeWindow);
    const completionProblem = getSooCoolCompletionProblem(task);
    const deliveredAtNeighbours = getSooCoolDeliveredAtNeighbours(task);
    const neighbourHouseNumber = getSooCoolNeighbourHouseNumber(task);
    const address = task?.address;
    const contactInfo = task?.contactInfo;

    return (
      <Descriptions
        key={task?.taskId ?? index}
        bordered
        size="small"
        column={1}
        className="mb-3"
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              {t("soocool.search.task_detail")} {task?.taskId ? `#${task.taskId}` : ""}
            </span>
            {renderTaskState(task?.taskState, task?.taskType)}
          </div>
        }
      >
        <Descriptions.Item label={t("soocool.search.task_type")}>
          {task?.taskType ? t(`soocool.task_type.${task.taskType}`, { defaultValue: task.taskType }) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("soocool.search.requested_time_window")}>{requested || "-"}</Descriptions.Item>
        <Descriptions.Item label={t("soocool.planned_time_window")}>{planned || "-"}</Descriptions.Item>
        <Descriptions.Item
          label={task?.taskType === SOOCOOL_TASK_TYPE_PICKUP ? t("soocool.pickup_address") : t("delivery_address")}
        >
          {address ? (
            <>
              {!!formatSooCoolAddressee(address) && (
                <>
                  {formatSooCoolAddressee(address)}
                  <br />
                </>
              )}
              {address.street} {address.houseNumber}
              <br />
              {address.postCode} {address.city}
              {!!address.country && `, ${address.country}`}
              {!!address.location && (
                <>
                  <br />
                  {address.location}
                </>
              )}
            </>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("soocool.search.contact")}>
          {contactInfo ? (
            <>
              {!!contactInfo.phone && (
                <>
                  {contactInfo.phone}
                  <br />
                </>
              )}
              {!!contactInfo.mobile && (
                <>
                  {contactInfo.mobile}
                  <br />
                </>
              )}
              {contactInfo.email || ""}
            </>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("soocool.search.instructions")}>{task?.instructions || "-"}</Descriptions.Item>
        {deliveredAtNeighbours && (
          <Descriptions.Item label={t("soocool.delivered_at_neighbours")}>
            {neighbourHouseNumber
              ? t("soocool.delivered_at_neighbours_house_number", { houseNumber: neighbourHouseNumber })
              : t("yes")}
          </Descriptions.Item>
        )}
        {!!completionProblem && (
          <Descriptions.Item label={t("soocool.completion_problem")}>
            <Tag color="volcano">
              {t(`soocool.completion_problem_value.${completionProblem}`, { defaultValue: completionProblem })}
            </Tag>
          </Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  const renderExpandedRow = (soocoolOrder) => {
    const goods = soocoolOrder?.goods || [];
    const tasks = soocoolOrder?.tasks || [];

    return (
      <div>
        {tasks.length > 0 ? tasks.map(renderTask) : <Empty description={t("soocool.search.no_tasks")} />}
        <div className="table-responsive">
          <h4>{t("soocool.colli_heading", { count: goods.length })}</h4>
          <Table
            showSorterTooltip={false}
            columns={colliColumns}
            dataSource={goods}
            rowKey={(good, index) => good?.goodId ?? good?.barcode ?? index}
            pagination={false}
            size="small"
            bordered={true}
            className="w-100"
            locale={{ emptyText: <Empty description={t("soocool.search.no_colli")} /> }}
          />
        </div>
      </div>
    );
  };

  return (
    <Card title={t("soocool.search.title")}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <span style={{ fontWeight: 500 }}>{t("soocool.search.shop")}</span>
        <Radio.Group value={shopSource} onChange={(e) => changeShop(e.target.value)} buttonStyle="solid">
          <Radio.Button value="VCE">Vleescentrum</Radio.Button>
          <Radio.Button value="HVO">Halalvlees Online</Radio.Button>
        </Radio.Group>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <Radio.Group value={searchMode} onChange={(e) => setSearchMode(e.target.value)}>
          <Radio.Button value={SEARCH_MODE_DATE}>{t("soocool.search.by_date")}</Radio.Button>
          <Radio.Button value={SEARCH_MODE_REFERENCE}>{t("soocool.search.by_order_reference")}</Radio.Button>
          <Radio.Button value={SEARCH_MODE_BARCODE}>{t("soocool.search.by_barcode")}</Radio.Button>
        </Radio.Group>

        {searchMode === SEARCH_MODE_DATE && (
          <>
            <Select value={dateField} onChange={setDateField} style={{ width: 160 }}>
              <Select.Option value={DATE_FIELD_DELIVERY}>{t("soocool.search.delivery_date")}</Select.Option>
              <Select.Option value={DATE_FIELD_PICKUP}>{t("soocool.search.pickup_date")}</Select.Option>
            </Select>
            <DatePicker
              value={selectedDate}
              onChange={(date) => setSelectedDate(date || dayjs())}
              locale={locale}
              format={DATE_FORMAT_DD_MM_YYYY}
              allowClear={false}
            />
          </>
        )}

        {searchMode === SEARCH_MODE_REFERENCE && (
          <Input
            value={orderReference}
            onChange={(e) => setOrderReference(e.target.value)}
            onPressEnter={handleSearch}
            placeholder={t("soocool.search.order_reference_placeholder")}
            style={{ width: 240 }}
          />
        )}

        {searchMode === SEARCH_MODE_BARCODE && (
          <Input
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onPressEnter={handleSearch}
            placeholder={t("soocool.search.barcode_placeholder")}
            style={{ width: 240 }}
          />
        )}

        <Button type="primary" icon={<SearchOutlined />} onClick={() => handleSearch()} loading={isLoading}>
          {t("search")}
        </Button>
        <Button
          danger
          icon={<StopOutlined />}
          disabled={selectedOrderIds.length === 0}
          loading={cancelling}
          onClick={confirmCancel}
        >
          {t("soocool.search.cancel_button", { count: selectedOrderIds.length })}
        </Button>
      </div>

      {!!cancelOutcome?.error && <Alert type="error" showIcon className="mb-4" message={cancelOutcome.error} />}
      {cancelOutcome?.cancelled?.length > 0 && (
        <Alert
          type="success"
          showIcon
          closable
          className="mb-4"
          message={t("soocool.search.cancelled_summary", { count: cancelOutcome.cancelled.length })}
          description={cancelOutcome.cancelled.map((result) => (
            <div key={result.sooCoolOrderId}>{describeCancelResult(result)}</div>
          ))}
        />
      )}
      {cancelOutcome?.failed?.length > 0 && (
        <Alert
          type="warning"
          showIcon
          closable
          className="mb-4"
          message={t("soocool.search.cancel_failed_summary", { count: cancelOutcome.failed.length })}
          description={cancelOutcome.failed.map((result) => (
            <div key={result.sooCoolOrderId}>{describeCancelResult(result)}</div>
          ))}
        />
      )}

      {!!error && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={error}
          description={looksLikeAuthProblem(error) ? t("soocool.search.auth_hint") : undefined}
        />
      )}

      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={columns}
          dataSource={results}
          rowKey={(soocoolOrder, index) => soocoolOrder?.orderId ?? soocoolOrder?.orderReference ?? index}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 1200 }}
          rowSelection={{
            selectedRowKeys: selectedOrderIds,
            onChange: (keys) => setSelectedOrderIds(keys),
            // Already cancelled, or no SooCool id to cancel by: nothing to select
            getCheckboxProps: (soocoolOrder) => ({ disabled: !!soocoolOrder?.cancelled || soocoolOrder?.orderId == null }),
          }}
          expandable={{ expandedRowRender: renderExpandedRow }}
          locale={{
            emptyText: (
              <Empty
                description={
                  error
                    ? t("soocool.search.failed_empty")
                    : hasSearched
                    ? t("soocool.search.no_results")
                    : t("soocool.search.start_hint")
                }
              />
            ),
          }}
        />
      </div>
    </Card>
  );
};

export default SooCool;
